// Ordered sandbox block editor for proposal library pages.

import { PROPOSAL_TEXT_MAX_LEN } from "@/lib/opportunity/proposalTextSections";
import {
  INTENT_AUTOFILL_VARIABLE_KEYS,
  INTENT_AUTOFILL_VARIABLE_LABELS,
  INTENT_FILL_KINDS,
  type IntentFillKind,
} from "@/lib/opportunity/proposalIntentLayout";
import {
  PROPOSAL_SANDBOX_BLOCK_LABELS,
  PROPOSAL_SANDBOX_TEXT_VARIANTS,
  PROPOSAL_SANDBOX_TEXT_VARIANT_LABELS,
  createCalcSummaryBlock,
  createCoverClosingBlock,
  createCoverLetterheadBlock,
  createCustomerSampleBlock,
  createFillFieldBlock,
  createPaymentMilestonesBlock,
  createRichTextBlock,
  createSignaturePartyBlock,
  moveSandboxBlock,
  type ProposalSandboxBlock,
  type ProposalSandboxBlockType,
  type ProposalSandboxFillFieldBlock,
  type ProposalSandboxRichTextBlock,
  type ProposalSandboxSignaturePartyBlock,
  type ProposalSandboxTextVariant,
} from "@/lib/opportunity/proposalSandboxBlocks";
import { ProposalSandboxPartyFieldsEditor } from "./ProposalSandboxPartyFieldsEditor";

type Props = {
  blocks: ProposalSandboxBlock[];
  onChange: (blocks: ProposalSandboxBlock[]) => void;
  addableTypes: readonly ProposalSandboxBlockType[];
};

export function ProposalSandboxBlockEditor({ blocks, onChange, addableTypes }: Props) {
  function updateBlock(blockId: string, next: ProposalSandboxBlock) {
    onChange(blocks.map((b) => (b.id === blockId ? next : b)));
  }

  function removeBlock(blockId: string) {
    onChange(blocks.filter((b) => b.id !== blockId));
  }

  function addBlock(type: ProposalSandboxBlockType) {
    switch (type) {
      case "rich_text":
        onChange([...blocks, createRichTextBlock({ text: "" })]);
        break;
      case "customer_sample":
        onChange([...blocks, createCustomerSampleBlock(true)]);
        break;
      case "signature_party":
        onChange([...blocks, createSignaturePartyBlock()]);
        break;
      case "fill_field":
        onChange([...blocks, createFillFieldBlock()]);
        break;
      case "cover_letterhead":
        onChange([...blocks, createCoverLetterheadBlock(true)]);
        break;
      case "cover_closing":
        onChange([...blocks, createCoverClosingBlock(true)]);
        break;
      case "calc_summary":
        onChange([...blocks, createCalcSummaryBlock(true)]);
        break;
      case "payment_milestones":
        onChange([...blocks, createPaymentMilestonesBlock(true)]);
        break;
    }
  }

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/60 p-3 dark:border-zinc-700 dark:bg-zinc-900/40">
      <div>
        <p className="text-xs font-medium text-gray-800 dark:text-zinc-200">Page blocks</p>
        <p className="mt-0.5 text-[11px] text-gray-500 dark:text-zinc-400">
          Reorderable starter structure. Structural blocks (tables, letterhead) pull live proposal
          data; text and fill blocks are editable. Stable ids prepare e-sign mapping.
        </p>
      </div>

      {blocks.map((block, idx) => (
        <div
          key={block.id}
          className="space-y-2 rounded border border-gray-200 bg-white p-2 dark:border-zinc-700 dark:bg-zinc-900"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-medium text-gray-700 dark:text-zinc-300">
              {PROPOSAL_SANDBOX_BLOCK_LABELS[block.type]}
            </span>
            <label className="flex items-center gap-1 text-[10px] text-gray-500">
              <input
                type="checkbox"
                checked={block.included !== false}
                onChange={(e) =>
                  updateBlock(block.id, { ...block, included: e.target.checked })
                }
              />
              Include
            </label>
            <span className="flex-1" />
            <button
              type="button"
              disabled={idx === 0}
              onClick={() => onChange(moveSandboxBlock(blocks, block.id, "up"))}
              className="rounded border border-gray-300 px-1.5 py-0.5 text-[11px] disabled:opacity-40 dark:border-zinc-600"
            >
              ↑
            </button>
            <button
              type="button"
              disabled={idx === blocks.length - 1}
              onClick={() => onChange(moveSandboxBlock(blocks, block.id, "down"))}
              className="rounded border border-gray-300 px-1.5 py-0.5 text-[11px] disabled:opacity-40 dark:border-zinc-600"
            >
              ↓
            </button>
            <button
              type="button"
              onClick={() => removeBlock(block.id)}
              className="rounded border border-red-300 px-1.5 py-0.5 text-[11px] text-red-700 dark:border-red-800 dark:text-red-300"
            >
              Remove
            </button>
          </div>

          {block.type === "rich_text" ? (
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-[11px] text-gray-600 dark:text-zinc-400">
                <span>Style</span>
                <select
                  value={block.variant === "header" ? "header" : "paragraph"}
                  onChange={(e) =>
                    updateBlock(block.id, {
                      ...block,
                      variant: e.target.value as ProposalSandboxTextVariant,
                    } satisfies ProposalSandboxRichTextBlock)
                  }
                  className="rounded border border-gray-200 px-1.5 py-1 text-[11px] dark:border-zinc-700 dark:bg-zinc-800"
                  aria-label="Text style"
                >
                  {PROPOSAL_SANDBOX_TEXT_VARIANTS.map((v) => (
                    <option key={v} value={v}>
                      {PROPOSAL_SANDBOX_TEXT_VARIANT_LABELS[v]}
                    </option>
                  ))}
                </select>
              </label>
              <textarea
                value={block.text}
                onChange={(e) =>
                  updateBlock(block.id, {
                    ...block,
                    text: e.target.value.slice(0, PROPOSAL_TEXT_MAX_LEN),
                  })
                }
                rows={block.variant === "header" ? 2 : 5}
                className={
                  "w-full rounded border border-gray-200 px-2 py-1.5 dark:border-zinc-700 dark:bg-zinc-800 " +
                  (block.variant === "header"
                    ? "text-sm font-semibold"
                    : "text-sm")
                }
              />
            </div>
          ) : null}

          {block.type === "customer_sample" ? (
            <p className="text-[11px] text-gray-500">
              Auto customer / project / price / deposit / revision sample.
            </p>
          ) : null}
          {block.type === "cover_letterhead" ? (
            <p className="text-[11px] text-gray-500">
              Date, customer address, subject line, and greeting.
            </p>
          ) : null}
          {block.type === "cover_closing" ? (
            <p className="text-[11px] text-gray-500">
              Sincerely block with sender contact from proposal settings.
            </p>
          ) : null}
          {block.type === "calc_summary" ? (
            <p className="text-[11px] text-gray-500">
              Cost / calculation table from format calc lines and pricing snapshot.
            </p>
          ) : null}
          {block.type === "payment_milestones" ? (
            <p className="text-[11px] text-gray-500">
              Payment milestone table from format payment schedule rules.
            </p>
          ) : null}

          {block.type === "signature_party" ? (
            <ProposalSandboxPartyFieldsEditor
              block={block}
              onChange={(next: ProposalSandboxSignaturePartyBlock) =>
                updateBlock(block.id, next)
              }
            />
          ) : null}

          {block.type === "fill_field" ? (
            <FillFieldBlockEditor
              block={block}
              onChange={(next) => updateBlock(block.id, next)}
            />
          ) : null}
        </div>
      ))}

      <div className="flex flex-wrap gap-1">
        {addableTypes.map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => addBlock(type)}
            className="rounded border border-dashed border-gray-300 px-2.5 py-1 text-[11px] text-gray-600 dark:border-zinc-600 dark:text-zinc-300"
          >
            + {PROPOSAL_SANDBOX_BLOCK_LABELS[type]}
          </button>
        ))}
      </div>
    </div>
  );
}

function FillFieldBlockEditor(props: {
  block: ProposalSandboxFillFieldBlock;
  onChange: (block: ProposalSandboxFillFieldBlock) => void;
}) {
  const { block, onChange } = props;
  return (
    <div className="grid gap-1.5 sm:grid-cols-[7rem_1fr_8rem]">
      <select
        value={block.kind}
        onChange={(e) =>
          onChange({ ...block, kind: e.target.value as IntentFillKind })
        }
        className="rounded border border-gray-200 px-1.5 py-1 text-[11px] dark:border-zinc-700 dark:bg-zinc-800"
      >
        {INTENT_FILL_KINDS.map((k) => (
          <option key={k} value={k}>
            {k}
          </option>
        ))}
      </select>
      <input
        type="text"
        value={block.label}
        onChange={(e) => onChange({ ...block, label: e.target.value })}
        className="rounded border border-gray-200 px-2 py-1 text-[11px] dark:border-zinc-700 dark:bg-zinc-800"
        aria-label="Fill label"
      />
      <select
        value={block.variable_key ?? ""}
        onChange={(e) =>
          onChange({ ...block, variable_key: e.target.value || null })
        }
        className="rounded border border-gray-200 px-1.5 py-1 text-[11px] dark:border-zinc-700 dark:bg-zinc-800"
      >
        <option value="">No autofill</option>
        {INTENT_AUTOFILL_VARIABLE_KEYS.map((k) => (
          <option key={k} value={k}>
            {INTENT_AUTOFILL_VARIABLE_LABELS[k]}
          </option>
        ))}
      </select>
    </div>
  );
}
